/**
 * src/components/FeedbackWidget.tsx
 *
 * Floating "Send feedback" button + modal. Mounts once inside AppShell and
 * self-gates on authentication. Submissions go through reporter.submitFeedback
 * which writes to the Harper Feedback table (or queues locally on failure).
 *
 * Placement: fixed bottom-right. Offsets above the mobile BottomTabBar via
 * the same --bottom-tab-height variable AppShell pads with, and above the
 * browser's safe-area inset on devices with home indicators.
 */

import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { MessageCircle, X } from './icons';
import { useAuth } from '../context/AuthContext';
import { PrimaryButton, SecondaryButton } from './ui';
import { submitFeedback } from '../reporting/reporter';

type WidgetState = 'closed' | 'open' | 'submitting' | 'thanks';

/**
 * Custom event name for opening the widget from outside the component
 * (e.g., the "Send feedback" row on /profile/help). Dispatch with:
 *   window.dispatchEvent(new CustomEvent(FEEDBACK_OPEN_EVENT))
 */
export const FEEDBACK_OPEN_EVENT = 'attic:open-feedback';

export function FeedbackWidget() {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<WidgetState>('closed');
  const [text, setText] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const thanksTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-focus the textarea when the modal opens — WEB ONLY. On the native
  // app (Capacitor/Android) a programmatic focus does NOT raise the soft
  // keyboard, and the already-"focused" field then swallows the user's tap so
  // the keyboard never appears. Leaving it unfocused lets the tap (a real
  // gesture) bring the keyboard up normally.
  useEffect(() => {
    if (state === 'open' && !Capacitor.isNativePlatform()) {
      const id = setTimeout(() => textAreaRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [state]);

  // Close on Escape when modal is open.
  useEffect(() => {
    if (state !== 'open') return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [state]);

  // Open from anywhere via window event — used by /profile/help "Send
  // feedback" so callers don't need a ref to the widget. Setting state
  // ignores no-op events (already open / submitting / thanks).
  useEffect(() => {
    function onOpen() {
      setState((prev) => (prev === 'closed' ? 'open' : prev));
    }
    window.addEventListener(FEEDBACK_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(FEEDBACK_OPEN_EVENT, onOpen);
  }, []);

  // Clear any pending thanks-auto-close timer on unmount.
  useEffect(() => {
    return () => { if (thanksTimerRef.current) clearTimeout(thanksTimerRef.current); };
  }, []);

  if (!isAuthenticated) return null;

  function close() {
    setState('closed');
    setText('');
  }

  async function handleSubmit() {
    const body = text.trim();
    if (!body) return;
    setState('submitting');
    await submitFeedback(body);
    setState('thanks');
    setText('');
    thanksTimerRef.current = setTimeout(() => setState('closed'), 2400);
  }

  return (
    <>
      {/* Floating launcher button */}
      {state === 'closed' && (
        <button
          type="button"
          data-testid="feedback-widget-launcher"
          aria-label="Send feedback"
          onClick={() => setState('open')}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 'calc(16px + var(--bottom-tab-height, 0px) + env(safe-area-inset-bottom, 0px))',
            width: 48,
            height: 48,
            borderRadius: 'var(--attic-r-pill)',
            background: 'var(--attic-terracotta)',
            color: '#FFFFFF',
            border: 'none',
            boxShadow: 'var(--attic-sh-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 90,
          }}
        >
          <MessageCircle size={20} aria-hidden="true" />
        </button>
      )}

      {/* Modal backdrop + sheet */}
      {(state === 'open' || state === 'submitting' || state === 'thanks') && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
          data-testid="feedback-widget-modal"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(35, 24, 15, 0.4)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 100,
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 480,
              background: 'var(--surface, #FFFAF1)',
              borderRadius: 'var(--attic-r-md)',
              boxShadow: 'var(--attic-sh-3)',
              padding: '20px 20px 16px',
              marginBottom: 'calc(var(--bottom-tab-height, 0px) + env(safe-area-inset-bottom, 0px) + var(--kb-inset, 0px))',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{
                fontFamily: 'var(--attic-font-display)',
                fontSize: 'var(--attic-t-display-sm)',
                fontWeight: 600,
                color: 'var(--attic-ink)',
                letterSpacing: '-0.3px',
              }}>
                {state === 'thanks' ? 'Thanks!' : 'Send feedback'}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={close}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--attic-ink-3)',
                  cursor: 'pointer',
                  padding: 4,
                  lineHeight: 0,
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            {state === 'thanks' ? (
              <p style={{
                fontSize: 'var(--attic-t-body)',
                color: 'var(--attic-ink-2)',
                margin: 0,
                lineHeight: 1.45,
              }}>
                We read every note. Luke will follow up if needed.
              </p>
            ) : (
              <>
                <p style={{
                  fontSize: 'var(--attic-t-body-sm)',
                  color: 'var(--attic-ink-2)',
                  margin: '0 0 10px',
                  lineHeight: 1.45,
                }}>
                  What's on your mind? Bugs, wishes, confusion — all welcome.
                </p>
                <textarea
                  ref={textAreaRef}
                  data-testid="feedback-widget-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={state === 'submitting'}
                  rows={5}
                  placeholder="A few sentences is plenty."
                  style={{
                    width: '100%',
                    fontFamily: 'var(--attic-font-body)',
                    fontSize: 'var(--attic-t-body)',
                    color: 'var(--attic-ink)',
                    background: '#FFFFFF',
                    border: '1px solid var(--attic-ink-4)',
                    borderRadius: 'var(--attic-r-sm)',
                    padding: '10px 12px',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    minHeight: 100,
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                  <SecondaryButton onClick={close} disabled={state === 'submitting'}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={state === 'submitting' || text.trim().length === 0}
                    data-testid="feedback-widget-submit"
                  >
                    {state === 'submitting' ? 'Sending…' : 'Send'}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
